import { HttpClient } from '@angular/common/http';
import { ChangeDetectorRef } from '@angular/core';
import { PaginationQueryRequest, PaginationQueryResponse } from '@vsolv/dev-kit/ngx';
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  map,
  of,
  shareReplay,
  startWith,
  Subject,
  switchMap,
  tap,
} from 'rxjs';

export type ConnectedTableDataSourceQueryParams = {
  [param: string]: string | number | boolean | ReadonlyArray<string | number | boolean>;
};

export class ConnectedTableDataSource<Item> {
  private _firstLoad$ = new BehaviorSubject(true);
  public readonly firstLoad$ = this._firstLoad$.asObservable();

  private _loading$ = new BehaviorSubject(true);
  public readonly loading$ = this._loading$.asObservable();

  private _error$ = new BehaviorSubject<Error | null>(null);
  public readonly error$ = this._error$.asObservable();

  private _pagination$ = new BehaviorSubject({ limit: 10, page: 1 });
  public readonly pagination$ = this._pagination$.asObservable();

  private _endpoint$ = new BehaviorSubject<string | null>(null);

  private _queryParams$ = new BehaviorSubject<ConnectedTableDataSourceQueryParams>({});

  private _refresh$ = new Subject<void>();

  private pageData$ = combineLatest([
    this._pagination$.pipe(distinctUntilChanged((p, n) => JSON.stringify(p) === JSON.stringify(n))),
    this._endpoint$,
    this._queryParams$.pipe(distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr))),
    this._refresh$.pipe(startWith(null)),
  ]).pipe(
    tap(() => this._loading$.next(true)),

    debounceTime(250),
    tap(([pagination, _, params]) => this.callbacks?.pageRequested?.(pagination, params)),

    switchMap(([{ limit, page }, endpoint, params]) => {
      if (!endpoint) return of(null);

      return this.http.get<PaginationQueryResponse<Item>>(endpoint, {
        params: {
          limit: limit.toString(),
          page: page.toString(),
          ...params,
        },
      });
    }),

    catchError(err => (this._error$.next(err), of(null))),

    tap(data => {
      this._loading$.next(false);
      this._firstLoad$.next(false);
      this.callbacks?.pageLoaded?.(data);
    }),

    shareReplay(1)
  );

  public readonly meta$ = this.pageData$.pipe(
    map(data => data?.meta ?? null),
    shareReplay(1)
  );

  public readonly isEmpty$ = this.meta$.pipe(
    map(meta => meta?.totalItems === 0),
    shareReplay(1)
  );

  public readonly items$ = this.pageData$.pipe(
    map(data => data?.items ?? []),
    tap(() => setTimeout(() => this.changeDetector.markForCheck())),
    shareReplay(1)
  );

  constructor(
    private http: HttpClient,
    private changeDetector: ChangeDetectorRef,
    private callbacks?: {
      pageRequested?: (request: PaginationQueryRequest, queryParams: ConnectedTableDataSourceQueryParams) => void;
      pageLoaded?: (response: PaginationQueryResponse<Item> | null) => void;
    }
  ) {}

  setEndpoint(endpoint: string | null) {
    this._endpoint$.next(endpoint);
  }

  setPage(page: number) {
    this._pagination$.next({ page, limit: this._pagination$.value.limit });
  }

  setLimit(limit: number) {
    this._pagination$.next({ limit, page: this._pagination$.value.page });
  }

  setQueryParams(queryParams: ConnectedTableDataSourceQueryParams) {
    // Preserve internal sorting params while merging new external params
    const { sortBy, sortOrder } = this._queryParams$.value;
    const sortParams = { ...(sortBy && { sortBy }), ...(sortOrder && { sortOrder }) };
    this._queryParams$.next({ ...queryParams, ...sortParams });
  }

  setQueryParam(key: string, value: string | null) {
    const { [key]: _, ...params } = this._queryParams$.value;
    this._queryParams$.next({ ...params, ...(value === null ? {} : { [key]: value }) });
  }

  refresh() {
    this._refresh$.next();
  }

  dispose() {
    this._error$.complete();
    this._refresh$.complete();
    this._loading$.complete();
    this._endpoint$.complete();
    this._firstLoad$.complete();
    this._pagination$.complete();
    this._queryParams$.complete();
  }
}
